import { DEFAULT_HEADER_CONFIG, HEADER_FIXED_CONFIG, HeaderFixedConfig } from "@/constants/header-config"

/**
 * Utility functions for header behavior management
 */

/**
 * Lấy cấu hình header cho locale cụ thể
 */
export const getHeaderConfig = (locale: string): HeaderFixedConfig => {
  return HEADER_FIXED_CONFIG[locale] || DEFAULT_HEADER_CONFIG
}

/**
 * Kiểm tra xem path có match với exact paths không
 */
const matchesExactPath = (pathname: string, exactPaths: string[]): boolean => {
  return exactPaths.some(path => pathname === path)
}

/**
 * Kiểm tra xem path có match với prefixes không
 */
const matchesPrefix = (pathname: string, prefixes: string[]): boolean => {
  return prefixes.some(prefix => pathname.startsWith(prefix))
}

/**
 * Kiểm tra xem header có nên được fixed không
 * @param pathname - Đường dẫn hiện tại
 * @param locale - Locale hiện tại
 * @returns true nếu header nên được fixed, false nếu không
 */
export const shouldHeaderBeFixed = (pathname: string, locale: string): boolean => {
  const config = getHeaderConfig(locale)

  // Kiểm tra excluded paths trước (priority cao hơn)
  if (matchesExactPath(pathname, config.excludedExactPaths)) {
    return false
  }

  if (matchesPrefix(pathname, config.excludedPrefixes)) {
    return false
  }

  // Kiểm tra fixed paths
  if (matchesExactPath(pathname, config.fixedExactPaths)) {
    return true
  }

  if (matchesPrefix(pathname, config.fixedPrefixes)) {
    return true
  }

  // Mặc định không fixed
  return false
}

/**
 * Kiểm tra xem header có nên transparent không
 * @param pathname - Đường dẫn hiện tại
 * @param locale - Locale hiện tại
 * @param isScrolled - Trạng thái scroll
 * @returns true nếu header nên transparent, false nếu không
 */
export const shouldHeaderBeTransparent = (pathname: string, locale: string, isScrolled: boolean): boolean => {
  // Chỉ transparent khi ở homepage hoặc trang self-guided-tour và chưa scroll
  const isHomePage = pathname === `/${locale}` || pathname === "/" || pathname === "/en" || pathname === "/vi"
  const isBikeRentalPage = pathname === "/bike-rental" || pathname === "/vi/bike-rental-vi"
  const isSelfGuidedTourPage = pathname === "/en-self-guieded-tour-new" || pathname === "/vi/self-guided-tour-vi"
  const isAllToursPage = pathname === "/all-tours" || pathname === "/vi/all-tours-vi"
  return (isHomePage || isSelfGuidedTourPage || isBikeRentalPage || isAllToursPage) && !isScrolled
}

/**
 * Lấy className cho header dựa trên trạng thái
 * @param pathname - Đường dẫn hiện tại
 * @param locale - Locale hiện tại
 * @param isScrolled - Trạng thái scroll
 * @param isMenuOpen - Trạng thái menu
 * @returns className string
 */
export const getHeaderClassName = (
  pathname: string,
  locale: string,
  isScrolled: boolean,
  isMenuOpen: boolean,
): string => {
  const shouldBeFixed = shouldHeaderBeFixed(pathname, locale)
  const shouldBeTransparent = shouldHeaderBeTransparent(pathname, locale, isScrolled)

  const baseClasses = "w-full flex flex-col items-center transition-all duration-300 ease-in-out"
  const fixedClasses = shouldBeFixed ? "fixed top-0 z-[9999]" : "sticky top-0 z-[9999]"
  const backgroundClasses =
    isMenuOpen || isScrolled ? "bg-white shadow-lg" : shouldBeTransparent ? "bg-transparent" : "bg-white shadow-lg"

  const isTourDetailPage = pathname.includes("/tours")
  return `${baseClasses} ${fixedClasses} ${backgroundClasses} ${
    isTourDetailPage ? "!shadow-none border-b border-gray-200" : ""
  }`.trim()
}

/**
 * Lấy className cho header content
 * @param pathname - Đường dẫn hiện tại
 * @param locale - Locale hiện tại
 * @param isScrolled - Trạng thái scroll
 * @param isMenuOpen - Trạng thái menu
 * @returns className string
 */
export const getHeaderContentClassName = (
  pathname: string,
  locale: string,
  isScrolled: boolean,
  isMenuOpen: boolean,
): string => {
  const shouldBeFixed = shouldHeaderBeFixed(pathname, locale)
  const shouldBeTransparent = shouldHeaderBeTransparent(pathname, locale, isScrolled)

  const baseClasses =
    "items-center flex flex-row justify-between px-4 py-5 sm:py-4 sm:px-8 w-full z-20 transition-all duration-300 ease-in-out"
  const positionClasses = shouldBeFixed ? "fixed top-0" : ""
  const backgroundClasses =
    isMenuOpen || isScrolled
      ? "bg-white shadow-lg backdrop-blur-md"
      : shouldBeTransparent
      ? "bg-transparent"
      : "bg-white shadow-lg backdrop-blur-md"

  return `${baseClasses} ${positionClasses} ${backgroundClasses}`.trim()
}

export const removeVietnameseDiacritics = (str: string): string => {
  if (!str) return ""
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
}
